/**
 * 报表页 —— **K 域 K1–K4**（四段全时间轴）
 *
 * 依据：原型 `docs/03-UX与UI设计/13-报表_原型.html`（四段 + 每段 1 张主图 + 三层财务健康）·
 * K 域 PRD **§4.1**（四段结构）· **§4.2**（K1）· **§4.3**（K2）· **§4.5**（K4）· **§4.8**
 * （三条规格铁律：双层吸顶 / 卡片 vs 下钻 / 下钻空态）· `DR-013` / `DR-015`。
 *
 * ## 🔴 端侧零口径（本页一个数都不算）
 *
 * | 段 | 数字来源 | 复用的东西 |
 * |---|---|---|
 * | ① 已发生 | `GET /api/report` 的 `spent`（趋势 / 同环比 / 日均 / 分类树 / 异常） | `ui/ReportCategoryTree.tsx` |
 * | ② 现在 | 🔴 **净资产另调 `GET /api/networth`**（PRD §4.1 原文"复用同一组件、同一函数"）；健康三层来自 `report.health` | `ui/NetWorthChart.tsx` · `ui/ReportHealthLines.tsx` |
 * | ③ 近期 | 🔴 **不自建** —— 复用 `getSpendable()` + `getUpcoming()`（`AC-K3.5`：与预算页逐分恒等） | `ui/SpendableCard.tsx` |
 * | ④ 远期 | **P1 占位**（`AC-K5.10`） | 文案常量 |
 *
 * ## ⚠️ 语义标题用 MUI 的 `variant`（渲染成 `h2`–`h6`）
 *
 * 铁律 41：**不要**手写 `<h3>` 去凑吸顶导航 —— 本页把段标题交给 MUI 语义标题，
 * `AnchorBar` 的选择器已放宽到 `h2`–`h6`（㊷ 批的教训）。
 *
 * ## ⚠️ 本批**不含**（诚实登记）
 * - **分类占比下钻抽屉**（`AC-K2.9`/`K2.10` 的宿主）：需先接"按分类取流水"，属后续批次；
 * - **③ 段的预算进度卡**：`ui/BudgetCard.tsx` 需要 `onToggle` / `onDrillUnpaid` 两个交互回调，
 *   端侧**没有只读变体** ⇒ 本批给"可花卡 + 必付清单 + 去计划页"的引用块方案，
 *   **不画点不动的控件**（同 ㊱ 批「不画灰项」纪律）；
 * - **`AnchorBar` 的无头几何取证**：本页未做（㊷ 批要求凡挂 AnchorBar 的页面须取几何证据）。
 *
 * ## 🆕 批 `109`：① 段的时间段选择
 *
 * 三档（近 3 / 6 / 12 个月）放在「钱花到哪儿去了」标题右侧；**档位在 URL 上**（`?months=`），
 * 起止日一律服务端算（`report.window`）。⚠️ 换档位会同时影响 ② 段的健康线（同一份窗口）——
 * 这是**故意的**：一页四段说的是同一个"这段时间"，窗口不一致比"少了两个指标"更难解释。
 * 🔴 未做：**自定义起止日**（服务端 `from`+`to` 已支持，端侧缺一对日期选择器 —— 登记在交接文档）。
 */

import { useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CurveRange, type CurveRange as CurveRangeType } from '@jiaying/shared';
import { getNetWorth, getReport, getUpcoming, listBudgets } from '../api/endpoints.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Amount } from '../ui/Amount.js';
import { BudgetCard } from '../ui/BudgetCard.js';
import { Loading } from '../ui/Loading.js';
import { NetWorthChart } from '../ui/NetWorthChart.js';
import { ReportCategoryDrawer, type DrillTarget } from '../ui/ReportCategoryDrawer.js';
import { ReportCategoryTree } from '../ui/ReportCategoryTree.js';
import { ReportHealthLines } from '../ui/ReportHealthLines.js';
import { SpendableCard } from '../ui/SpendableCard.js';
import {
  EMPTY_SPENT_HINT,
  FX_INCOMPLETE_NOTE,
  P1_NOTE,
  P1_TITLE,
  REPORT_DEFAULT_MONTHS,
  SEGMENT_TITLES,
  TREND_MONTH_OPTIONS,
  changeText,
  reportWindowText,
  trendBars,
  trendMonthLabel,
} from '../features/report-labels.js';
import { findCategoryAmount } from '../features/report.js';

export function ReportPage() {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState<CurveRangeType>(CurveRange.M12);
  /**
   * 🆕 **报表窗口的月数**（批 `109` · 负责人 2026-10-26：报表要能选时间段）。
   *
   * ## 🔴 为什么放在 URL 而不是 `useState`
   *
   * ① **可刷新 / 可分享**：客户说"我看的是近 3 个月"时，链接能带过去；
   * ② **可被静态渲染测到**：`renderToStaticMarkup` 不能点击 ⇒ 用 `useState` 时
   *    "3 个月档"这个状态**永远无法断言**（第一版就是这么红的）。
   *
   * 🔴 端侧只选**档位**，起止日一律服务端算（`report.window`）——
   * 自己拼 `from`/`to` 会踩"半开 / 闭区间"与账本时区两个坑（铁律 17）。
   */
  const [search, setSearch] = useSearchParams();
  const monthsParam = Number(search.get('months'));
  const months = (TREND_MONTH_OPTIONS as readonly number[]).includes(monthsParam)
    ? monthsParam
    : REPORT_DEFAULT_MONTHS;
  const setMonths = (m: number): void => {
    const next = new URLSearchParams(search);
    next.set('months', String(m));
    setSearch(next, { replace: true });
  };

  // 🔴 queryKey 带 `months`：换档位即换缓存，且 `invalidateQueries(['report'])` 仍按前缀命中
  const report = useQuery({ queryKey: ['report', months], queryFn: () => getReport({ months }) });
  const netWorth = useQuery({ queryKey: ['netWorth'], queryFn: getNetWorth });
  const upcoming = useQuery({ queryKey: ['upcoming'], queryFn: getUpcoming });
  /**
   * 🆕 ③ 段预算执行：🔴 **与预算页同一接口**（`AC-K3.5` 数字逐分恒等由"同一份下发"构造性成立），
   *     并经 `ui/BudgetCard.tsx` **同一组件**渲染（`AC-K3.6`，不复制实现）。
   */
  const budgets = useQuery({ queryKey: ['budgets'], queryFn: listBudgets });
  /** 🆕 下钻目标（`AC-K2.9`：点分类行 → 抽屉列该分类的流水明细） */
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  if (report.isPending) return <Loading py={3} />;
  if (report.isError) {
    return <Alert severity="error">报表读取失败：{String(report.error)}</Alert>;
  }

  const view = report.data;
  const currency = view.spent.currency;
  const bars = trendBars(view.spent.trend);

  return (
    <Stack spacing={3} ref={contentRef}>
      <AnchorBar contentRef={contentRef} />

      {/* ─────────────────── ① 已发生：钱花到哪儿去了 ─────────────────── */}
      <Box component="section" data-anchor={SEGMENT_TITLES.spent} data-testid="segment-spent">
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}
        >
          <Typography variant="h6">{SEGMENT_TITLES.spent}</Typography>
          {/*
            🆕 时间段档位（批 `109`）—— 只换 `months`，起止日服务端算。
            🔴 用 `Chip` + `data-on` 与流水筛选同一形态（不是新样式）。
          */}
          <Stack direction="row" spacing={0.5} data-testid="report-range">
            {TREND_MONTH_OPTIONS.map((m) => (
              <Chip
                key={m}
                size="small"
                label={trendMonthLabel(m)}
                color={months === m ? 'primary' : 'default'}
                variant={months === m ? 'filled' : 'outlined'}
                data-testid={`report-range-${String(m)}`}
                data-on={months === m ? 'true' : 'false'}
                onClick={() => {
                  setMonths(m);
                }}
              />
            ))}
          </Stack>
        </Stack>
        {/* 🔴 必须把**实际窗口**写出来：换档位后"日均 / 环比"的分母都变了 */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }} data-testid="report-window">
          {reportWindowText(view.window)}
        </Typography>

        <Card sx={{ mb: 1.5 }}>
          <CardContent>
            {/* 主图 = 分类占比（见下方组件）；本卡承载「趋势 + 同环比 + 日均」的陈述 */}
            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  窗口总支出
                </Typography>
                <Amount cents={view.spent.totalExpense as never} currency={currency} variant="h5" colored={false} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  环比（上一期）
                </Typography>
                <Typography variant="h6" data-testid="report-mom">
                  {changeText(view.spent.momE4)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  同比（去年同期）
                </Typography>
                <Typography variant="h6" data-testid="report-yoy">
                  {changeText(view.spent.yoyE4)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  窗口内日均（{view.spent.windowDays} 天）
                </Typography>
                <Amount cents={view.spent.dailyAverage as never} currency={currency} variant="h6" colored={false} />
              </Box>
            </Stack>

            {/* 趋势：按月（🔴 零填充；无支出的月显示 0 而非留空 —— 见 `monthsInWindow` 的说明） */}
            {bars.length === 0 || view.spent.totalExpense === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} data-testid="trend-empty">
                {EMPTY_SPENT_HINT}
              </Typography>
            ) : (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="flex-end"
                sx={{ mt: 2, height: 64 }}
                data-testid="report-trend"
              >
                {bars.map((b) => (
                  <Box key={b.month} sx={{ flex: 1, textAlign: 'center' }} data-testid={`trend-${b.month}`}>
                    <Box
                      sx={{
                        height: `${String(Math.max(2, b.percent))}%`,
                        minHeight: 2,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {b.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <ReportCategoryTree
          nodes={view.spent.categories}
          currency={currency}
          totalCents={view.spent.totalExpense}
          fxIncomplete={view.spent.fxIncomplete}
          // 🆕 下钻入口（`AC-K2.9`）—— 不传则行是纯文本（不给点不动的控件）
          onDrill={(node) => {
            setDrill(node);
          }}
        />

        {view.spent.fxIncomplete ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {FX_INCOMPLETE_NOTE}
          </Alert>
        ) : null}

        {/*
          🆕 批 141：**简化记账的工资**会让「收入」与别期不可比（`DR-016` §3.6 / C 域 §5.2.1⑤）。
          🔴 只提示、不改数字：收入仍按用户记的算，但必须让人知道**分母不完整** ——
          否则"这个月收入怎么少了"会被误读成降薪。
        */}
        {view.simplifiedSalaryCount > 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="report-simplified-salary">
            本期有 {String(view.simplifiedSalaryCount)} 笔工资按「简化记账」记 ——
            收入用的是<b>到手金额</b>，未含社保与个税。跨期比较收入 / 储蓄率时，
            这几笔与「完整工资条」（收入按税前）不是同一口径。
          </Alert>
        ) : null}

        {/*
          🆕 下钻抽屉（`AC-K2.9` / `AC-K2.10`）—— 与卡片**同一窗口**、**同一查询**、**同一行文案**。
          🔴 卡片金额由 `findCategoryAmount()` 从**同一份**服务端树里取（端侧只查找、不算）。
        */}
        <ReportCategoryDrawer
          target={drill}
          window={{ from: view.window.from, to: view.window.to }}
          currency={currency}
          cardCents={findCategoryAmount(view.spent.categories, drill?.id ?? null)}
          onClose={() => {
            setDrill(null);
          }}
        />
      </Box>

      {/* ─────────────────── ② 现在：家底什么样 ─────────────────── */}
      <Box component="section" data-anchor={SEGMENT_TITLES.now} data-testid="segment-now">
        <Typography variant="h6" sx={{ mb: 1 }}>
          {SEGMENT_TITLES.now}
        </Typography>

        {netWorth.isPending ? <Loading py={2} /> : null}
        {netWorth.isError ? (
          <Alert severity="error">净资产读取失败：{String(netWorth.error)}</Alert>
        ) : null}
        {netWorth.data === undefined ? null : (
          <>
            <Card sx={{ mb: 1.5 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" component="div">
                  净资产（复用 J 域同一函数 / 同一接口）
                </Typography>
                <Amount
                  cents={netWorth.data.netWorth as never}
                  currency={netWorth.data.baseCurrency}
                  variant="h4"
                  colored={false}
                  data-testid="report-networth"
                />
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      总资产
                    </Typography>
                    <Amount
                      cents={netWorth.data.totalAssets as never}
                      currency={netWorth.data.baseCurrency}
                      variant="body2"
                      colored={false}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      总负债
                    </Typography>
                    <Amount
                      cents={netWorth.data.totalLiabilities as never}
                      currency={netWorth.data.baseCurrency}
                      variant="body2"
                      colored={false}
                    />
                  </Box>
                </Stack>
                {/* 🔴 未就绪来源诚实标注（`notReady`）—— 不让"接口没写"伪装成 0 */}
                {netWorth.data.partial ? (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                    含 {netWorth.data.notReady.length} 项尚未就绪的构成，合计为下界。
                  </Typography>
                ) : null}
              </CardContent>
            </Card>

            {/* 下钻：净资产趋势（🔴 **只画真实快照** —— 组件内部已保证不插值不平推） */}
            <Card sx={{ mb: 1.5 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  净资产趋势（下钻）
                </Typography>
                <NetWorthChart range={range} onRangeChange={setRange} />
              </CardContent>
            </Card>
          </>
        )}

        {/* 财务健康三层（🔴 层数由服务端决定，端侧不补空层） */}
        <ReportHealthLines lines={view.health.lines} currency={currency} />
      </Box>

      {/* ─────────────────── ③ 近期：这个月撑得住吗 ─────────────────── */}
      <Box component="section" data-anchor={SEGMENT_TITLES.soon} data-testid="segment-soon">
        <Typography variant="h6" sx={{ mb: 1 }}>
          {SEGMENT_TITLES.soon}
        </Typography>

        {/* 🆕 预算执行（`AC-K3.1` / `K3.5` / `K3.6` / `K3.7`）—— 🔴 **与预算页同一组件 + 同一接口** */}
        <Card data-testid="report-budget">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              预算执行（引用 G 域同一份下发 · 与预算页同一组件）
            </Typography>
            {budgets.isPending ? <Loading py={2} /> : null}
            {budgets.isError ? (
              <Typography variant="body2" color="text.secondary" data-testid="report-budget-error">
                预算读取失败：{String(budgets.error)}
              </Typography>
            ) : null}
            {budgets.data === undefined ? null : budgets.data.items.length === 0 ? (
              /* 🔴 `AC-K3.7`：无预算 ⇒ **引导文案**，不显示进度/可花数字 */
              <Typography variant="body2" color="text.secondary" data-testid="report-budget-empty" sx={{ mt: 0.5 }}>
                还没有设置预算 —— 去计划页建一个，这里就会显示执行进度与时间刻度（不预设数字，也不显示空的进度条）。
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }} data-testid="report-budget-list">
                {/* 🔴 **只读变体**：不传 `onToggle` / `onDrillUnpaid` ⇒ 不渲染开关与下钻按钮
                    （信息照旧显示：档位、已用%、时间刻度、含待缴） */}
                {budgets.data.items.map((c) => (
                  <BudgetCard key={c.id} card={c} />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* 🔴 引用块（不是自算）：可花卡 = J3 的同一份下发 */}
        <Box sx={{ mb: 1.5, mt: 1.5 }}>
          <SpendableCard />
        </Box>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              未来 30 天必付（引用 F2 同一份聚合）
            </Typography>
            {upcoming.isPending ? <Loading py={2} /> : null}
            {upcoming.isError ? (
              <Typography variant="body2" color="text.secondary">
                必付读取失败：{String(upcoming.error)}
              </Typography>
            ) : null}
            {upcoming.data === undefined ? null : upcoming.data.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" data-testid="upcoming-empty">
                未来 30 天没有已安排的必付项。
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 1 }} data-testid="report-upcoming">
                {upcoming.data.items.slice(0, 5).map((it) => (
                  <Stack key={it.id} direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="caption">
                      {it.dueDate} · {it.label}
                    </Typography>
                    {/* 🔴 缺汇率 ⇒ 显示**原币**并让币种自己说话（`baseCents = null` 时不按 1:1 冒充） */}
                    {it.baseCents === null ? (
                      <Amount cents={it.originalCents as never} currency={it.currency} variant="caption" colored={false} />
                    ) : (
                      <Amount
                        cents={it.baseCents as never}
                        currency={upcoming.data?.baseCurrency ?? currency}
                        variant="caption"
                        colored={false}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
            <Button size="small" sx={{ mt: 1 }} onClick={() => void navigate('/plan')} data-testid="report-to-plan">
              去计划页看预算执行
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* ─────────────────── ④ 远期：这样下去会怎样（P1） ─────────────────── */}
      <Box component="section" data-anchor={SEGMENT_TITLES.future} data-testid="segment-future">
        <Typography variant="h6" sx={{ mb: 1 }}>
          {SEGMENT_TITLES.future}
        </Typography>
        <Card data-testid="report-p1">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {P1_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {P1_NOTE}
            </Typography>
            {/* 🔴 本段是**刻意的结构占位** —— 服务端 `future.phase = 'p1'` 让端侧能区分
                "这段是留的" 与 "这段忘了做"。不放"敬请期待"式空话，也不放假图。 */}
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              （当前阶段：{view.future.phase}）
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
